// ============================================================
// priceLines — 見積もりの内訳を名前付きで返す（2026-11-03 A1・N2）
//
// 起きたこと:
//   A1（10/16〜22・最多）: calc の base_price（1日目の定価）を期間の基本料金と取り違え、
//       「基本 ¥24,000・割引 -¥13,200・合計 ¥10,800」「合計 ¥26,000（基本料金 ¥9,000）」と書いた（合計は正しい）
//   N2（11/01〜02）: 早朝返却の深夜早朝手数料 ¥3,300 を「Handling Fee / Tax」「その他費用（免責等）」「手数料等込み」と書き、
//       英語の見積もりで「早朝料金は自動計算に含まれない」と事実でない説明をした
// 対策: ツールの結果に「名前の付いた内訳（price_lines）」と一行の式（price_summary）を入れ、その名前で書かせる。
//       1日目の定価は daily_list_price と名前を変え、期間の基本料金と並べない。
// 内訳の正は calc API（new-n-car frontend/public/calc）。fees = 深夜早朝手数料（クルーズ船の返却の時刻料金を含む）
// ============================================================

import type { BookingCalcBreakdown } from '../api/bookingCalcService';

export interface PriceLine { label: string; amount: number }

const yen = (n: number) => `¥${Math.round(n).toLocaleString()}`;

/**
 * 1台分の内訳。noc / cdw は calculate_price が分けた額を渡す（無ければ breakdown の値、それも無ければ insurance を「補償」1行で）
 * 0 円の行は出さない（基本料金と合計は必ず出す）
 */
export function priceLines(b: Partial<BookingCalcBreakdown>, ins?: { noc?: number; cdw?: number }): PriceLine[] {
  const n = (v: any) => Number(v) || 0;
  const lines: PriceLine[] = [{ label: b.holiday_pack ? '基本料金（連休パックを含む・割引前）' : '基本料金（期間・割引前）', amount: n(b.subtotal_base) }];
  if (n(b.discount) > 0) lines.push({ label: b.coupon_applied ? 'クーポン割引' : 'キャンペーン割引', amount: -n(b.discount) });
  if (n(b.fees) > 0) lines.push({ label: '深夜早朝手数料', amount: n(b.fees) });
  if (n(b.options) > 0) lines.push({ label: 'オプション（チャイルドシート等）', amount: n(b.options) });
  const noc = ins?.noc ?? b.noc;
  const cdw = ins?.cdw ?? b.cdw;
  if (noc != null || cdw != null) {
    if (n(noc) > 0) lines.push({ label: 'NOC（休業補償の免除）', amount: n(noc) });
    if (n(cdw) > 0) lines.push({ label: 'CDW（免責補償）', amount: n(cdw) });
  } else if (n(b.insurance) > 0) {
    lines.push({ label: '補償（NOC・CDW）', amount: n(b.insurance) });
  }
  if (n(b.location_surcharge) > 0) lines.push({ label: '送迎料（平良港）', amount: n(b.location_surcharge) });
  lines.push({ label: '合計', amount: n(b.grand_total) });
  return lines;
}

/** 一行の式（例: 基本料金（期間・割引前） ¥63,000 + 深夜早朝手数料 ¥3,300 = 合計 ¥66,300） */
export function priceSummary(lines: PriceLine[]): string {
  const body = lines.filter((l) => l.label !== '合計');
  const total = lines.find((l) => l.label === '合計');
  return body.map((l, i) => `${i === 0 ? '' : l.amount < 0 ? ' − ' : ' + '}${l.label} ${yen(Math.abs(l.amount))}`).join('')
    + (total ? ` = 合計 ${yen(total.amount)}` : '');
}

/** 内訳の足し算が合計と合うか（合わなければ内訳を出さず合計だけ使わせる） */
export function linesAddUp(lines: PriceLine[]): boolean {
  const total = lines.find((l) => l.label === '合計')?.amount ?? 0;
  const sum = lines.filter((l) => l.label !== '合計').reduce((s, l) => s + l.amount, 0);
  return Math.abs(sum - total) < 1;
}

/** ツールの結果に入れる形（足し算が合わないときは、内訳を書かず合計だけ使うよう注記） */
export function priceBreakdownFields(b: Partial<BookingCalcBreakdown>, ins?: { noc?: number; cdw?: number }) {
  const lines = priceLines(b, ins);
  return linesAddUp(lines)
    ? { price_lines: lines, price_summary: priceSummary(lines) }
    : { price_lines: lines, price_summary: `合計 ${yen(Number(b.grand_total) || 0)}`,
        price_lines_note: '内訳の足し算が合計と合わない。内訳は書かず合計だけを伝える' };
}

/** プロンプトに入れるルール */
export const PRICE_LINES_PROMPT = [
  '# 見積もりの内訳の書き方（2026-11-03）',
  '- 料金の内訳は、ツールの結果の **price_lines のラベルと金額のとおりに**書く（price_summary はその一行の式）。',
  '  ラベルを言い換えない: 深夜早朝手数料を「手数料」「Handling Fee」「税」「免責」「諸費用」などと書かない。英語なら "Late-night / early-morning fee"。',
  '- **daily_list_price は1日目の定価**。期間の基本料金として書かない・合計と並べない（期間の基本料金は price_lines の「基本料金」）。',
  '- 結果に無い料金（「早朝料金は含まれない」「CDW は1日 ¥〇〜¥〇」など）を作らない。補償込みの額が要るなら calculate_price を noc_waiver / cdw 付きで呼ぶ。',
].join('\n');
